import { isPlatformBrowser } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import {
  importProvidersFrom,
  isDevMode,
  provideZoneChangeDetection,
  PLATFORM_ID,
} from '@angular/core';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { META_REDUCERS, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideToastr } from 'ngx-toastr';

import { ROUTES } from './app.routes';
import { ApiLoaderInterceptorService } from './services/api-loader-interceptor.service';
import { ApiService } from './services/api.service';
import { EncryptionService } from './services/encryption.service';
import { APP_EFFECTS } from './store/app-effect';
import { REDUCERS } from './store/app-reducers';
import { createLocalStorageSyncReducer } from './store/meta-reducers';

export const APP_CONFIG: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      ROUTES,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      })
    ),
    provideClientHydration(withEventReplay()),
    provideHttpClient(withInterceptorsFromDi()),
    ApiService,
    provideStore(REDUCERS),
    provideEffects(APP_EFFECTS),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
    }),
    {
      provide: META_REDUCERS,
      multi: true,
      useFactory: (encryptionService: EncryptionService) =>
        createLocalStorageSyncReducer(encryptionService),
      deps: [EncryptionService],
    },
    provideToastr({
      positionClass: 'toast-bottom-full-width',
      closeButton: true,
      toastClass: 'custom-toast',
      titleClass: 'custom-toast-title',
      messageClass: 'custom-toast-msg',
      easeTime: 500,
      timeOut: 1000,
      extendedTimeOut: 1000,
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiLoaderInterceptorService,
      multi: true,
    },
    provideAnimationsAsync(),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        isolate: false,
      })
    ),
    {
      provide: 'APP_INITIALIZER',
      useFactory: (translate: TranslateService, platformId: object) => () => {
        translate.setDefaultLang('en');

        // Only access localStorage in browser environment
        if (isPlatformBrowser(platformId)) {
          const SAVED_LANG = localStorage.getItem('lang') || 'en';
          translate.use(SAVED_LANG);
        } else {
          // Server-side: use default language
          translate.use('en');
        }
      },
      deps: [TranslateService, PLATFORM_ID],
      multi: true,
    },
  ],
};
